import axios from 'axios';
import {
  Tenant,
  ApiKey,
  UsageMetrics,
  TenantMetrics,
  AiConfig,
  ThrottlingConfig,
  HealthStatus,
  CreateTenantRequest,
  UpdateTenantRequest,
  Device,
  CreateDeviceRequest,
  DeviceRegistrationResponse
} from '../types';

// Configure the API base URL - this should point to your Admin API Worker
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://flux-admin-api.lorien.workers.dev';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const adminApi = {
  // Authentication
  setAuthToken: (token: string) => {
    localStorage.setItem('admin_token', token);
  },

  clearAuthToken: () => {
    localStorage.removeItem('admin_token');
  },

  // Tenant Management
  getTenants: async (): Promise<Tenant[]> => {
    const response = await api.get('/api/tenants');
    return response.data;
  },

  createTenant: async (tenant: CreateTenantRequest): Promise<Tenant> => {
    const response = await api.post('/api/tenants', tenant);
    return response.data;
  },

  getTenant: async (tenantId: string): Promise<Tenant> => {
    const response = await api.get(`/api/tenants/${tenantId}`);
    return response.data;
  },

  updateTenant: async (tenantId: string, updates: UpdateTenantRequest): Promise<Tenant> => {
    const response = await api.put(`/api/tenants/${tenantId}`, updates);
    return response.data;
  },

  deleteTenant: async (tenantId: string): Promise<void> => {
    await api.delete(`/api/tenants/${tenantId}`);
  },

  validateTenant: async (tenantId: string): Promise<{ valid: boolean; details?: Tenant }> => {
    const response = await api.post('/api/tenants/validate', { tenant_id: tenantId });
    return response.data;
  },

  // API Key Management
  generateApiKey: async (tenantId: string): Promise<{ api_key: string; tenant_id: string }> => {
    const response = await api.post('/api/keys', { tenant_id: tenantId });
    return response.data;
  },

  revokeApiKey: async (keyId: string): Promise<void> => {
    await api.delete(`/api/keys/${keyId}`);
  },

  // System Monitoring
  getUsageMetrics: async (): Promise<UsageMetrics> => {
    const response = await api.get('/api/metrics/usage');
    return response.data;
  },

  getTenantMetrics: async (): Promise<TenantMetrics[]> => {
    const response = await api.get('/api/metrics/tenants');
    return response.data;
  },

  // Operational Management
  getAiConfig: async (): Promise<AiConfig> => {
    const response = await api.get('/api/config/ai');
    return response.data;
  },

  updateAiConfig: async (config: AiConfig): Promise<AiConfig> => {
    const response = await api.put('/api/config/ai', config);
    return response.data;
  },

  getThrottlingConfig: async (): Promise<ThrottlingConfig> => {
    const response = await api.get('/api/config/throttling');
    return response.data;
  },

  updateThrottlingConfig: async (config: ThrottlingConfig): Promise<ThrottlingConfig> => {
    const response = await api.put('/api/config/throttling', config);
    return response.data;
  },

  // Device Management
  getTenantDevices: async (tenantId: string): Promise<Device[]> => {
    const response = await api.get(`/api/tenants/${tenantId}/devices`);
    return response.data;
  },

  registerDevice: async (tenantId: string, device: CreateDeviceRequest): Promise<DeviceRegistrationResponse> => {
    const response = await api.post(`/api/tenants/${tenantId}/devices`, device);
    return response.data;
  },

  getDevice: async (deviceId: string): Promise<Device> => {
    const response = await api.get(`/api/devices/${deviceId}`);
    return response.data;
  },

  updateDevice: async (deviceId: string, updates: Partial<Device>): Promise<Device> => {
    const response = await api.put(`/api/devices/${deviceId}`, updates);
    return response.data;
  },

  deleteDevice: async (deviceId: string): Promise<void> => {
    await api.delete(`/api/devices/${deviceId}`);
  },

  // Health Check
  getHealthStatus: async (): Promise<HealthStatus> => {
    const response = await api.get('/api/health');
    return response.data;
  }
};

export default adminApi;
